/* eslint-disable jsx-a11y/anchor-has-content */
import { NavLink, Link } from 'react-router-dom'

import { GetText, useGetText } from 'shared/i18n'
import { default as Button } from 'shared/ui/button'
import { Tab, default as TabNav } from 'shared/ui/tab-nav'
import Title from './title'

export default function GetInvolvedPage() {
  const { gettext } = useGetText()
  return (
    <>
      <Title title={gettext('Get Involved')} backgroundVariant={8} />

      <div className="container mx-auto px-4 pb-24 pt-16 text-base leading-relaxed md:text-lg md:leading-8">
        <div className="mx-auto lg:w-2/3 2xl:w-1/2">
          <TabNav>
            <Tab type="link" href="#experts-wanted">
              <GetText>Experts Wanted</GetText>
            </Tab>
            <Tab type="link" href="#call-for-editors">
              <GetText>Call for Editors</GetText>
            </Tab>
          </TabNav>

          <section id="experts-wanted">
            <article className="mb-12">
              <h2 className="mb-4 font-serif text-3xl font-semibold text-primary-darkest">
                <GetText>Experts Wanted</GetText>
              </h2>

              <p className="mb-4">
                <GetText>
                  The Database of Religious History (DRH) is currently
                  recruiting historians, archaeologists, and religious studies
                  scholars in all areas of expertise (PhD students and above).
                  If you have academic expertise in the history or anthropology
                  of religion, please consider contributing to the DRH.
                </GetText>
              </p>

              <div className="mb-4 flex justify-center bg-black/5 py-6">
                <Button type="router-link" to="/signup" color="success">
                  <GetText>Sign up as expert</GetText>
                </Button>
              </div>
            </article>

            <article className="mb-12">
              <h2 className="mb-4 font-serif text-3xl font-semibold text-primary-darkest">
                <GetText>How Can I Get Involved?</GetText>
              </h2>

              <p className="mb-4">
                <GetText>
                  The DRH is built upon the knowledge of our expert contributor
                  community. You can help by adding your own expertise in the
                  form of a new entry or by commenting on existing entries.
                </GetText>
              </p>

              <p className="mb-4">
                <GetText
                  components={{
                    link: <Link to="/landing/polls" className="link" />,
                  }}
                >
                  Each entry is built around answers to [link]one of our
                  polls[/link]. A poll is a list of questions organized around a
                  particular unit of analysis, such as religious group, a
                  religious place, a text, etc.
                </GetText>
              </p>

              <p>
                <GetText>There are two ways to contribute:</GetText>
              </p>

              <ul className="mb-4 ml-8 list-disc">
                <li>
                  <GetText>
                    by creating an entry and answering questions for a new
                    entry,
                  </GetText>
                </li>
                <li>
                  <GetText>by commenting upon existing entries.</GetText>
                </li>
              </ul>

              <p className="mb-4">
                <GetText>
                  An entry can be thought of as analogous to an encyclopedia
                  article that summarizes the state of the field on a particular
                  topic.
                </GetText>
              </p>

              <p className="mb-4">
                <GetText>
                  Experts create an entry by defining its name, region and date
                  range. Then they proceed by answering questions from the poll.
                  The answers are mostly “Yes”, “No”, “Field Doesn’t Know”, “I
                  Don’t Know”. An expert can add comments and references (using
                  a built-in reference tool) to each answer to flesh out their
                  choice or provide further reading, and upload images and even
                  videos to flesh out their responses. Experts can also simply
                  skip questions (or entire sections of the questionnaire) that
                  are irrelevant or outside of their knowledge. We are aiming to
                  survey your expert intuitions and not expecting you to have to
                  perform new research to answer the questionnaire.
                </GetText>
              </p>

              <p className="mb-4">
                <GetText>
                  In addition to the questionnaire answers, we encourage
                  contributors to add qualitative comments and references, and
                  will soon be introducing tools that will allow you to
                  organize, and link to, your own or existing online qualitative
                  resources, such as text or image archives.
                </GetText>
              </p>
            </article>

            <article className="mb-12">
              <h2 className="mb-4 font-serif text-3xl font-semibold text-primary-darkest">
                <GetText>Do I qualify as an expert?</GetText>
              </h2>

              <p className="mb-4">
                <GetText
                  components={{
                    link: (
                      <NavLink
                        className="link"
                        to="/landing/about/people/editors"
                      />
                    ),
                  }}
                >
                  Contributors to the DRH must have academic expertise with
                  regard to the particular religious phenomenon for which they
                  prepare an entry. Our current rule of thumb is being ABD or
                  above in a relevant department or program, subject to approval
                  by the relevant [link]Editor[/link], but exceptions are
                  sometimes made at the discretion of the Editor.
                </GetText>
              </p>
            </article>

            <article className="mb-12">
              <h2 className="mb-4 font-serif text-3xl font-semibold text-primary-darkest">
                <GetText>Why should I participate?</GetText>
              </h2>

              <p className="mb-4">
                <GetText>
                  Your expertise will help to strengthen what we hope will
                  become a standard reference source for scholars, teachers,
                  students and the general public.
                </GetText>
              </p>

              <p className="mb-4">
                <GetText>
                  Join an academic community dedicated to sharing scholarly
                  knowledge in an entirely new fashion, on a radically
                  open-access platform. This includes access to all entries
                  currently published in the database.
                </GetText>
              </p>

              <p className="mb-4">
                <GetText
                  components={{
                    link: (
                      <a
                        className="link"
                        href="https://open.library.ubc.ca/search?q=contributor%253A%2520%2522Database%2520of%2520Religious%2520History%2520(DRH)%2522&p=0&sort=6&view=0&perPage=0&circle=n&dBegin=&dEnd=&c=1"
                        target="_blank"
                        rel="noreferrer"
                      />
                    ),
                  }}
                >
                  Contributions to the DRH are formal publications that are
                  recognized both on the database website and within the
                  database itself. Each is assigned a DOI and stored in PDF form
                  by the [link]UBC Library[/link] for ease of citation and
                  access. Contributors should also list their DRH entries on
                  their personal CVs.
                </GetText>
              </p>

              <p className="mb-4">
                <GetText
                  components={{
                    mail: (
                      <a
                        className="link"
                        href="mailto:project.manager@religiondatabase.org"
                      >
                        project.manager@religiondatabase.org
                      </a>
                    ),
                  }}
                >
                  The Database of Religious History pays a thank-you honorarium
                  to contributors of $300 CAD, subject to the availability of
                  funds and editor approval, and the entry being completed
                  within 60 days of creation. Please note that the DRH is a
                  grant-funded project, and honoraria availability is limited
                  and dependent upon outside sources – any questions about the
                  current status should be directed to [mail/].
                </GetText>
              </p>
            </article>

            <article className="mb-12">
              <h2 className="mb-4 font-serif text-3xl font-semibold text-primary-darkest">
                <GetText>Who has already contributed?</GetText>
              </h2>

              <p className="mb-4">
                <GetText
                  components={{
                    contributors: <NavLink className="link" to="/browse" />,
                    team: (
                      <NavLink
                        className="link"
                        to="/landing/about/people/team"
                      />
                    ),
                  }}
                >
                  Although the project is in the early stages of development, a
                  growing number of distinguished historians have agreed to help
                  us with the data collection in their respective fields of
                  expertise. A list of participating DRH experts can be found on
                  our [contributors]contributors page[/contributors]. Our
                  [team]Editorial Team[/team] help us to coordinate the
                  increasing pool of contributors.
                </GetText>
              </p>
            </article>

            <article className="mb-12">
              <h2 className="mb-4 font-serif text-3xl font-semibold text-primary-darkest">
                <GetText>How do I start?</GetText>
              </h2>

              <p className="mb-4">
                <GetText>
                  The process of entering data on your area of expertise is both
                  intuitive and flexible. After signing up you can get started
                  right away: please read the instructions on the Getting
                  Started page, and then click on Contribute to begin creating
                  your entry. The interface is self-explanatory, and you can run
                  through the data entry procedure at your own pace, when you
                  have moments of free time, over the course of a 3 month
                  period. Once you are approved by the Editor you have chosen
                  during the sign up process (this usually only takes a few
                  days) you will be able to publish your entry and also begin
                  commenting on other entries.
                </GetText>
              </p>

              <p className="mb-4 flex justify-center bg-black/5 py-6">
                <NavLink
                  className="rounded-md bg-success px-5 py-4 uppercase tracking-widest text-white transition-colors hover:bg-success-dark"
                  to="/signup"
                >
                  <GetText>Sign up as expert</GetText>
                </NavLink>
              </p>

              <p className="mb-4">
                <GetText
                  components={{
                    link: (
                      <a
                        className="link"
                        href="mailto:project.manager@religiondatabase.org"
                      />
                    ),
                  }}
                >
                  If you have any questions or concerns about the approval
                  process, or have not received a timely response to your
                  sign-up request, please feel free to contact our
                  [link]Managing Editor[/link].
                </GetText>
              </p>

              {/* <p className="mb-4">
              <GetText
                components={{
                  link: (
                    <a
                      className="link"
                      href="http://hecc.ubc.ca/the-database-of-religious-history/drh-call-for-editors/"
                      target="_blank"
                      rel="noreferrer"
                    />
                  ),
                }}
              >
                We’re recruiting editors, too. Please read our [link]Call for
                Editors[/link].
              </GetText>
            </p> */}
            </article>
          </section>

          <section id="call-for-editors">
            <article className="mb-12">
              <h2 className="mb-4 font-serif text-3xl font-semibold text-primary-darkest">
                <GetText>Call for Editors</GetText>
              </h2>

              <p className="mb-4">
                <GetText>
                  The Database of Religious History (DRH) is seeking qualified
                  scholars to serve on our editorial team. Successful applicants
                  would have comprehensive knowledge of religious practice or
                  belief in the ancient or pre-modern world. Additionally, the
                  applicant should have a good grasp of the relevant scholars in
                  their field to aid in recruitment and management of an active
                  body of contributors. Junior scholars are particularly
                  encouraged.
                </GetText>
              </p>

              <p className="mb-4">
                <GetText
                  components={{
                    link: (
                      <NavLink
                        className="link"
                        to="/landing/about/people/editors"
                      />
                    ),
                  }}
                >
                  See [link]here[/link] for a current list of editors.
                </GetText>
              </p>

              <p className="mb-4">
                <GetText>
                  The editor would seek to grow and expand the coverage of the
                  database by recruiting actively within their field, looking
                  for scholars with a particular expertise in areas currently
                  not covered by the database. In addition, the editor would vet
                  new sign-ups in the editor's area of expertise and manage the
                  contributions of experts, encouraging timely submission and
                  high-quality entries. In the wider community, the editor would
                  serve as an outward face of the DRH at conferences and
                  scholarly gatherings, both promoting the project and seeking
                  expert feedback.
                </GetText>
              </p>

              <p className="mb-4">
                <GetText
                  components={{
                    link: (
                      <a
                        className="link"
                        href="https://hecc2017.sites.olt.ubc.ca/files/2016/09/Editordutiesandresponsibilities.pdf"
                        target="_blank"
                        rel="noreferrer"
                      />
                    ),
                  }}
                >
                  For a thorough explanation of responsibilities, [link]download
                  this PDF[/link].
                </GetText>
              </p>
            </article>

            <article className="mb-12">
              <h2 className="mb-4 font-serif text-3xl font-semibold text-primary-darkest">
                <GetText>Baseline responsibility</GetText>
              </h2>

              <p className="mb-4 italic">
                <GetText>
                  This is the one obligatory duty of being a DRH Editor, and the
                  most important:
                </GetText>
              </p>

              <ul className="mb-4 list-disc pl-5">
                <li>
                  <GetText>
                    In a timely fashion (2-3 days maximum response time), vet
                    and approve or reject new area-specific potential experts
                    who identify you as the appropriate editor while signing up
                    for the DRH. This is a fairly simple process, our rule of
                    thumb is that a new expert can be deemed “qualified” if ABD
                    or above in a relevant field of study at a recognized
                    university, although exceptions can be made at the
                    discretion of the editor. Upon request, the Managing Editor
                    is available to cover new sign-up requests when individual
                    editors are away from e-mail because of travel or fieldwork.
                  </GetText>
                </li>
              </ul>
            </article>

            <article className="mb-12">
              <h2 className="mb-4 font-serif text-3xl font-semibold text-primary-darkest">
                <GetText>Other duties and responsibilities</GetText>
              </h2>

              <p className="mb-4 italic">
                <GetText>
                  These are other duties that an Editor could ideally undertake.
                  Obviously, Editors vary considerably in terms of how much time
                  they have for these other responsibilities, which are most
                  appealing, etc.
                </GetText>
              </p>

              <ul className="mb-4 list-disc pl-5">
                <li>
                  <GetText>Recruit new experts in your discipline</GetText>
                </li>
                <li>
                  <GetText>Manage and review contributions by experts</GetText>
                </li>
                <li>
                  <GetText>
                    Work closely with experts to answer questions and possibly
                    to help set up new entries
                  </GetText>
                </li>
                <li>
                  <GetText>
                    Record contact efforts for new recruits, correspondence with
                    current experts, and honorarium eligibility for contributors
                  </GetText>
                </li>
                <li>
                  <GetText>Contribute to the DRH</GetText>
                </li>
                <li>
                  <GetText>
                    Work with the Managing Editor to promote new features and
                    functions of the DRH
                  </GetText>
                </li>
                <li>
                  <GetText>
                    Represent and promote the DRH at academic meetings
                  </GetText>
                </li>
                <li>
                  <GetText>
                    Engage with the evolution of the database project and
                    development of new features
                  </GetText>
                </li>
              </ul>
            </article>

            <article className="mb-12">
              <h2 className="mb-4 font-serif text-3xl font-semibold text-primary-darkest">
                <GetText>Incentives</GetText>
              </h2>

              <ul className="mb-4 list-disc pl-5">
                <li>
                  <GetText>
                    $100 honorarium per supervised published entry
                  </GetText>
                </li>
                <li>
                  <GetText>Relevant conference travel assistance</GetText>
                </li>
                <li>
                  <GetText>
                    Credit on project’s website, on CV, and in publications
                  </GetText>
                </li>
                <li>
                  <GetText>
                    Official letter from the DRH to your department speaking to
                    your service to the field
                  </GetText>
                </li>
                <li>
                  <GetText>
                    Potential for teaching releases for active editors
                  </GetText>
                </li>
                <li>
                  <GetText>Increased visibility within your field</GetText>
                </li>
              </ul>

              <p className="mb-4">
                <GetText>
                  If you have any questions about the nature of the position or
                  its commitments, please do no hesitate to contact either the
                  Project Director or the Associate Director.
                </GetText>
              </p>
            </article>

            <article className="mb-12">
              <h2 className="mb-4 font-serif text-3xl font-semibold text-primary-darkest">
                <GetText>How to apply</GetText>
              </h2>

              <p className="mb-4">
                <GetText
                  components={{
                    link: (
                      <a
                        className="link"
                        href="mailto:willismonroe@gmail.com"
                        target="_blank"
                        rel="noreferrer"
                      />
                    ),
                  }}
                >
                  Send CV and cover letter to [link]Dr. Willis Monroe, Associate
                  Director[/link]. Decisions are made on a rolling basis.
                </GetText>
              </p>

              <p className="mb-4">
                <GetText>
                  Dr. Edward Slingerland, Project Director
                  (edward.slingerland@ubc.ca)
                </GetText>
              </p>
              <p className="mb-4">
                <GetText>
                  Dr. Willis Monroe, Associate Director (willismonroe@gmail.com)
                </GetText>
              </p>
            </article>
          </section>
        </div>
      </div>
    </>
  )
}
